import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Snackbar,
  Alert,
  LinearProgress,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomAxios from '../utils/CustomAxios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const COM_FILTER_LABELS = {
  communication_mode: 'Channel',
  mid: 'Member ID range',
  sh: 'Share balance range',
  sb: 'Subscription balance range',
  age: 'Age range',
  sex: 'Sex',
  race: 'Race',
  po: 'Photo',
  state: 'State',
  estate: 'Estate',
  region: 'Region',
  postal_code: 'Postal code',
  nomailer: 'No mailer',
  deceased: 'Deceased',
  dispAction: 'Disciplinary action',
  director: 'Director',
  observer: 'Observer',
  delegate: 'Delegate',
  company: 'Company',
  junior: 'Junior',
  terminated: 'Terminated',
};

function formatComFilterValue(key, value) {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (value.length === 2) return `${value[0]} – ${value[1]}`;
    return value.join(', ');
  }
  if (key === 'communication_mode' && typeof value === 'string') {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return String(value);
}

export function CommunicationCompose() {
  const navigate = useNavigate();
  const formPayload = useSelector((state) => state.executive.formPayload);

  const comFilters = useMemo(() => {
    return formPayload?.COM || formPayload?.['COM'] || {};
  }, [formPayload]);

  const recipientFilterRows = useMemo(() => {
    const skipKeys = new Set(['content']);
    return Object.entries(comFilters)
      .filter(([k]) => !skipKeys.has(k))
      .map(([k, v]) => {
        const display = formatComFilterValue(k, v);
        if (display === null) return null;
        return {
          id: k,
          label: COM_FILTER_LABELS[k] || k.replace(/_/g, ' '),
          value: display,
        };
      })
      .filter(Boolean);
  }, [comFilters]);

  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [mediaSource, setMediaSource] = useState('device');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaMeta, setMediaMeta] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [cloudMedia, setCloudMedia] = useState([]);
  const [loadingCloudMedia, setLoadingCloudMedia] = useState(false);
  const [campaignId, setCampaignId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [recipientPreviewCount, setRecipientPreviewCount] = useState(null);
  const [previewCountLoading, setPreviewCountLoading] = useState(false);
  const [previewCountError, setPreviewCountError] = useState('');
  const [campaignHistory, setCampaignHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const formatFilters = (item) => {
    const recipients = item?.filters?.recipients;
    if (recipients && typeof recipients === 'object' && !Array.isArray(recipients)) {
      return Object.entries(recipients)
        .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '');
    }
    return [];
  };

  useEffect(() => {
    if (!comFilters || Object.keys(comFilters).length === 0) {
      setToast({ open: true, message: 'Please apply Communication filters first.', severity: 'error' });
      return;
    }
    const mode = String(comFilters.communication_mode || '').toLowerCase();
    if (mode === 'whatsapp') {
      navigate('/communication/whatsapp-compose');
    }
  }, [comFilters, navigate]);

  useEffect(() => {
    if (!campaignId) return;

    const intervalId = setInterval(async () => {
      try {
        const res = await CustomAxios.get(`oasis/campaign/status/?campaign_id=${campaignId}`);
        const data = res?.data;
        if (data) {
          setProgress(data);
          if (data.status && data.status !== 'processing') {
            clearInterval(intervalId);
          }
        }
      } catch (e) {
        console.error('Communication status polling failed:', e);
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [campaignId]);

  const fetchCampaignHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await CustomAxios.get('oasis/campaign/history/?communication_mode=email&limit=10');
      setCampaignHistory(res?.data?.results || []);
    } catch (e) {
      setCampaignHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchCampaignHistory();
  }, []);

  const composedContent = useMemo(() => content, [content]);

  useEffect(() => {
    if (mediaSource !== 'cloud') return;
    let active = true;
    const fetchCloudMedia = async () => {
      setLoadingCloudMedia(true);
      try {
        const res = await CustomAxios.get('oasis/whatsapp/campaign-media/?media_type=file');
        if (active) {
          setCloudMedia(res?.data?.data || []);
        }
      } catch (e) {
        if (active) {
          setCloudMedia([]);
          const msg = e?.response?.data?.error || e.message || 'Failed to load cloud media.';
          setToast({ open: true, message: msg, severity: 'error' });
        }
      } finally {
        if (active) {
          setLoadingCloudMedia(false);
        }
      }
    };
    fetchCloudMedia();
    return () => {
      active = false;
    };
  }, [mediaSource]);

  const buildEmailPayload = () => {
    const recipients = recipientFilterRows.reduce((acc, row) => {
      acc[row.id] = row.value;
      return acc;
    }, {});
    return {
      ...comFilters,
      communication_mode: 'email',
      subject: subject || '[Oasis] Communication',
      content: composedContent,
      media_url: mediaUrl || undefined,
      media_name: mediaMeta?.name || undefined,
      media_content_type: mediaMeta?.contentType || undefined,
      recipients,
    };
  };

  const openSendConfirm = async () => {
    if (!content.trim() || recipientFilterRows.length === 0) return;
    setSendConfirmOpen(true);
    setRecipientPreviewCount(null);
    setPreviewCountError('');
    setPreviewCountLoading(true);
    try {
      const res = await CustomAxios.post('oasis/campaign_message/', {
        ...buildEmailPayload(),
        preview_only: true,
      });
      const n = res?.data?.recipient_count;
      setRecipientPreviewCount(typeof n === 'number' ? n : parseInt(String(n), 10) || 0);
    } catch (e) {
      setPreviewCountError(
        e?.response?.data?.message || e?.response?.data?.error || e.message || 'Could not load recipient count.'
      );
      setRecipientPreviewCount(null);
    } finally {
      setPreviewCountLoading(false);
    }
  };

  const closeSendConfirm = (event, reason) => {
    if (reason === 'backdropClick') return;
    if (sending) return;
    setSendConfirmOpen(false);
    setRecipientPreviewCount(null);
    setPreviewCountError('');
    setPreviewCountLoading(false);
  };

  const handleSend = async () => {
    if (!content.trim()) return;
    setSending(true);
    try {
      const payload = buildEmailPayload();

      const res = await CustomAxios.post('oasis/campaign_message/', payload);
      const id = res?.data?.campaign_id || res?.data?.campaignId || res?.data?.id;
      setCampaignId(id);
      setProgress(res?.data || null);
      fetchCampaignHistory();
      setToast({ open: true, message: 'Bulk email campaign started.', severity: 'success' });
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        'Failed to start bulk email campaign.';
      setToast({ open: true, message: msg, severity: 'error' });
    } finally {
      setSending(false);
      setSendConfirmOpen(false);
    }
  };

  const handleClearAndBack = () => {
    setContent('');
    setMediaSource('device');
    setMediaFile(null);
    setMediaUrl('');
    setMediaMeta(null);
    setCloudMedia([]);
    setCampaignId(null);
    setProgress(null);
    navigate('/communication/email');
  };

  const handleUploadFiles = async () => {
    if (!mediaFile) return;
    setUploadingFiles(true);
    try {
      const formData = new FormData();
      formData.append('file', mediaFile);
      formData.append('media_type', 'file');
      const res = await CustomAxios.post('oasis/whatsapp/campaign-media-upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadedUrl = res?.data?.media_url || '';
      if (!uploadedUrl) {
        throw new Error('Upload succeeded but media_url not returned.');
      }
      setMediaUrl(uploadedUrl);
      setMediaMeta({
        name: res?.data?.blob_name || mediaFile.name,
        contentType: res?.data?.content_type || mediaFile.type || 'application/octet-stream',
      });
      setMediaFile(null);
      setToast({ open: true, message: 'File uploaded successfully.', severity: 'success' });
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to upload files.';
      setToast({ open: true, message: msg, severity: 'error' });
    } finally {
      setUploadingFiles(false);
    }
  };

  return (
    <Box sx={{ width: '100%', marginTop: '25px', padding: '27px', backgroundColor: '#F8FAFC' }}>
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
      <Box
        component="form"
        sx={{ flex: { xs: '1 1 100%', lg: '0 0 70%' }, maxWidth: { xs: '100%', lg: '70%' }, display: 'flex', flexDirection: 'column', gap: '16px' }}
        onSubmit={(e) => {
          e.preventDefault();
          openSendConfirm();
        }}
      >
        <Box sx={{ mb: 0.5 }}>
          <Typography sx={{ color: 'var(--Pallate-green-500, #288d2c)', fontFamily: 'inter', fontSize: '24px', fontWeight: 500 }}>
            Communication
          </Typography>
          {campaignId ? (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ color: '#1E293B', fontFamily: 'inter', mb: 0.5 }}>
                Campaign {campaignId}
              </Typography>
              <Typography sx={{ color: '#1E293B', fontFamily: 'inter' }}>
                Queued: {progress?.queued || 0} | Sent: {progress?.sent_count || 0} | Failed: {progress?.failed_count || 0} | Pending: {progress?.pending_count || 0}
              </Typography>
              {progress?.status ? (
                <Chip
                  label={`Status: ${progress.status}`}
                  size="small"
                  sx={{
                    mt: 1,
                    width: 'fit-content',
                    fontFamily: 'inter',
                    bgcolor: progress.status === 'completed' ? '#ECFDF3' : '#F1F5F9',
                    color: progress.status === 'completed' ? '#047857' : '#334155',
                    border: '1px solid #CBD5E1',
                  }}
                />
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Typography sx={{ color: '#1E293B', fontFamily: 'inter', fontSize: '18px', fontWeight: 400, maxWidth: '672px', mb: 1 }}>
          Submit the bulk email content. Recipients match the filters below from the previous step.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '16px',
            borderRadius: '4px',
            border: '1px solid var(--Light-Forms-Border, #CBD5E1)',
            background: '#F8FAFC',
          }}
        >
          <Typography sx={{ fontFamily: 'inter', fontSize: '16px', fontWeight: 600, lineHeight: '24px', color: '#1E293B' }}>
            Recipient
          </Typography>
          {recipientFilterRows.length === 0 ? (
            <Typography sx={{ fontFamily: 'inter', fontSize: '14px', color: '#64748B' }}>
              No filters found. Use <strong>Back</strong> to apply filters on the Communication page.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {recipientFilterRows.map((row) => (
                <Box
                  key={row.id}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px 16px',
                    alignItems: 'baseline',
                    fontFamily: 'inter',
                    fontSize: '14px',
                    lineHeight: '22px',
                  }}
                >
                  <Typography component="span" sx={{ color: '#64748B', minWidth: { xs: '100%', sm: '180px' } }}>
                    {row.label}
                  </Typography>
                  <Typography component="span" sx={{ color: '#1E293B', fontWeight: 500, flex: 1 }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography sx={{ fontFamily: 'inter', fontSize: '16px', fontWeight: 400, lineHeight: '24px', color: '#1E293B' }}>
            Subject Line
          </Typography>
          <TextField
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            fullWidth
            size="small"
            sx={{
              background: 'var(--Light-Forms-Background, #FFF)',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography sx={{ fontFamily: 'inter', fontSize: '16px', fontWeight: 400, lineHeight: '24px', color: '#1E293B' }}>
            Email Content
          </Typography>
          <Box sx={{ border: '1px solid var(--Light-Forms-Border, #CBD5E1)', borderRadius: '4px', background: 'var(--Light-Forms-Background, #FFF)', padding: '12px' }}>
            <textarea
              value={content}
              placeholder="Please enter the email content here"
              wrap="soft"
              style={{
                width: '100%',
                outline: 'none',
                border: 'none',
                resize: 'none',
                background: 'transparent',
                color: 'var(--Light-Text-Secondary-Text, #64748B)',
                minHeight: '160px',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                lineHeight: '1.5',
                fontFamily: 'inter',
                fontSize: '14px',
              }}
              onChange={(e) => setContent(e.target.value)}
            />
          </Box>
          
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography sx={{ fontFamily: 'inter', fontSize: '16px', fontWeight: 400, lineHeight: '24px', color: '#1E293B' }}>
            Attach files
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, border: '1px solid #CBD5E1', borderRadius: 1, backgroundColor: '#FFF' }}>
            <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '13px' }}>
              Upload files from your device or reuse existing campaign media from cloud storage.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
              <Box
                onClick={() => {
                  setMediaSource('device');
                  setMediaFile(null);
                  setMediaUrl('');
                  setMediaMeta(null);
                }}
                sx={{
                  flex: '1 1 220px',
                  minWidth: 220,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: mediaSource === 'device' ? '1px solid #288d2c' : '1px solid #CBD5E1',
                  backgroundColor: mediaSource === 'device' ? '#F0FDF4' : '#FFF',
                  cursor: 'pointer',
                }}
              >
                <Typography sx={{ fontFamily: 'inter', fontSize: '14px', fontWeight: 600, color: '#1E293B' }}>
                  Upload from device
                </Typography>
                <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#64748B', mt: 0.5 }}>
                  Choose a file from your device and upload it for this communication.
                </Typography>
              </Box>
              <Box
                onClick={() => {
                  setMediaSource('cloud');
                  setMediaFile(null);
                  setMediaUrl('');
                  setMediaMeta(null);
                }}
                sx={{
                  flex: '1 1 220px',
                  minWidth: 220,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: mediaSource === 'cloud' ? '1px solid #288d2c' : '1px solid #CBD5E1',
                  backgroundColor: mediaSource === 'cloud' ? '#F0FDF4' : '#FFF',
                  cursor: 'pointer',
                }}
              >
                <Typography sx={{ fontFamily: 'inter', fontSize: '14px', fontWeight: 600, color: '#1E293B' }}>
                  Choose from cloud
                </Typography>
                <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#64748B', mt: 0.5 }}>
                  Reuse existing campaign files already uploaded to storage.
                </Typography>
              </Box>
            </Box>
            {mediaSource === 'device' ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px dashed #CBD5E1',
                  backgroundColor: '#F8FAFC',
                }}
              >
                <Typography sx={{ fontFamily: 'inter', color: '#1E293B', fontSize: '13px' }}>
                  {mediaFile ? mediaFile.name : 'No file selected'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    component="label"
                    variant="outlined"
                    sx={{ textTransform: 'none', minHeight: 40 }}
                  >
                    Choose file
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setMediaFile(file);
                        setMediaUrl('');
                        setMediaMeta(null);
                      }}
                    />
                  </Button>
                  {mediaFile ? (
                    <Button
                      variant="contained"
                      onClick={handleUploadFiles}
                      disabled={uploadingFiles}
                      sx={{
                        minHeight: 40,
                        background: '#288d2c',
                        '&:hover': { background: '#237a26' },
                        textTransform: 'none',
                        lineHeight: '16px',
                      }}
                    >
                      {uploadingFiles ? 'Uploading...' : 'Upload media'}
                    </Button>
                  ) : null}
                  {mediaFile ? (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setMediaFile(null);
                        setMediaUrl('');
                        setMediaMeta(null);
                      }}
                      disabled={uploadingFiles}
                      sx={{
                        minHeight: 40,
                        borderColor: '#CBD5E1',
                        color: '#1E293B',
                        textTransform: 'none',
                      }}
                    >
                      Remove file
                    </Button>
                  ) : null}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px solid #E2E8F0',
                  backgroundColor: '#F8FAFC',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'inter', color: '#1E293B', fontSize: '13px', fontWeight: 600 }}>
                    Campaign media
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      setLoadingCloudMedia(true);
                      try {
                        const res = await CustomAxios.get('oasis/whatsapp/campaign-media/?media_type=file');
                        setCloudMedia(res?.data?.data || []);
                      } catch (e) {
                        const msg = e?.response?.data?.error || e.message || 'Failed to load cloud media.';
                        setToast({ open: true, message: msg, severity: 'error' });
                      } finally {
                        setLoadingCloudMedia(false);
                      }
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Refresh
                  </Button>
                </Box>
                <Box
                  sx={{
                    maxHeight: 240,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  {(cloudMedia || []).map((item) => {
                    const selected = mediaUrl === item.media_url;
                    return (
                      <Box
                        key={item.media_url}
                        onClick={() => {
                          setMediaUrl(String(item.media_url));
                          setMediaMeta({
                            name: item.name,
                            contentType: item.content_type || 'file',
                          });
                          setMediaFile(null);
                        }}
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          border: selected ? '1px solid #288d2c' : '1px solid #CBD5E1',
                          backgroundColor: selected ? '#F0FDF4' : '#FFF',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontFamily: 'inter', color: '#1E293B', fontSize: '13px', fontWeight: 500 }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '12px', mt: 0.4 }}>
                            {item.content_type || 'file'}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 88,
                            height: "90%",
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '1px solid #CBD5E1',
                            backgroundColor: '#F8FAFC',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {String(item.content_type || '').startsWith('image/') ? (
                            <Box
                              component="img"
                              src={item.media_url}
                              alt={item.name}
                              sx={{ width: '50%', height: '50%', objectFit: 'cover' }}
                            />
                          ) : String(item.content_type || '').startsWith('video/') ? (
                            <Box
                              component="video"
                              src={item.media_url}
                              muted
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '11px', textAlign: 'center', px: 1 }}>
                              FILE
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                {loadingCloudMedia ? <LinearProgress sx={{ mt: 0.5 }} /> : null}
                {!loadingCloudMedia && cloudMedia.length === 0 ? (
                  <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '13px' }}>
                    No campaign media found in cloud.
                  </Typography>
                ) : null}
              </Box>
            )}
            {uploadingFiles ? <LinearProgress sx={{ mt: 0.5 }} /> : null}
            {mediaUrl ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  alignItems: 'flex-start',
                  p: 1.25,
                  borderRadius: 1,
                  background: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontFamily: 'inter', fontSize: '13px', color: '#166534', fontWeight: 500 }}>
                    {(mediaMeta && mediaMeta.name) || 'Selected file'}
                  </Typography>
                  <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#64748B', wordBreak: 'break-all' }}>
                    {mediaUrl}
                  </Typography>
                </Box>
                <Button
                  variant="text"
                  onClick={() => {
                    setMediaFile(null);
                    setMediaUrl('');
                    setMediaMeta(null);
                  }}
                  sx={{ textTransform: 'none', color: '#B91C1C' }}
                >
                  Remove
                </Button>
              </Box>
            ) : null}
          </Box>
          {(content || mediaUrl) ? (
            <Box sx={{ border: '1px solid #CBD5E1', borderRadius: '4px', background: '#F8FAFC', padding: '12px' }}>
              <Typography sx={{ fontFamily: 'inter', fontSize: '13px', fontWeight: 600, color: '#1E293B', mb: 1 }}>
                Preview
              </Typography>
              <Typography sx={{ fontFamily: 'inter', fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                {composedContent || 'Nothing to preview'}
              </Typography>
              {mediaUrl ? (
                <Box sx={{ mt: 1.5, p: 1.25, borderRadius: '6px', border: '1px solid #BBF7D0', background: '#F0FDF4' }}>
                  <Typography sx={{ fontFamily: 'inter', fontSize: '13px', color: '#166534', fontWeight: 500 }}>
                    {(mediaMeta && mediaMeta.name) || 'Selected media'}
                  </Typography>
                  <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#64748B' }}>
                    Media will be rendered separately in the email body.
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', mt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={sending || !content.trim() || recipientFilterRows.length === 0}
            sx={{
              display: 'flex',
              width: '104px',
              padding: '12px',
              justifyContent: 'center',
              gap: '8px',
              borderRadius: '4px',
              background: 'var(--Light-Utility-Primary, #288d2c)',
            }}
          >
            Send
            <SendIcon sx={{ ml: 0.5, width: '20px', height: '20px' }} />
          </Button>

          <Button
            variant="outlined"
            startIcon={<ClearAllIcon />}
            onClick={handleClearAndBack}
            sx={{
              borderColor: '#288d2c',
              color: '#288d2c',
              '&:hover': { borderColor: '#288d2c' },
            }}
          >
            Back
          </Button>
        </Box>

      </Box>
      <Box
        sx={{
          flex: { xs: '1 1 100%', lg: '0 0 30%' },
          maxWidth: { xs: '100%', lg: '30%' },
          p: 1.5,
          border: '1px solid #CBD5E1',
          borderRadius: 1,
          backgroundColor: '#FFF',
          height: { xs: 'auto', lg: 'calc(100vh - 120px)' },
          display: 'flex',
          flexDirection: 'column',
          position: { xs: 'static', lg: 'sticky' },
          top: { xs: 'auto', lg: 20 },
        }}
      >
        <Typography sx={{ fontFamily: 'inter', fontSize: '14px', fontWeight: 600, color: '#1E293B', mb: 1 }}>
          Email Campaign History
        </Typography>
        {loadingHistory ? (
          <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#64748B' }}>Loading…</Typography>
        ) : campaignHistory.length === 0 ? (
          <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#64748B' }}>No history yet.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, overflowY: 'auto', flex: 1 }}>
            {campaignHistory.map((item) => (
              <Accordion key={item.campaign_id} disableGutters elevation={0} sx={{ border: '1px solid #E2E8F0' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#1E293B', fontWeight: 600 }}>
                      #{item.campaign_id} - {item.status || 'processing'}
                    </Typography>
                    <Typography sx={{ fontFamily: 'inter', fontSize: '11px', color: '#64748B' }}>
                      Total: {item.total_recipients || 0} 
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography sx={{ fontFamily: 'inter', fontSize: '11px', color: '#1E293B', mb: 0.5 }}>
                    <strong>Details:</strong>
                  </Typography>
                  {formatFilters(item).length === 0 ? (
                    <Typography sx={{ fontFamily: 'inter', fontSize: '11px', color: '#64748B', mb: 0.5 }}>
                      -
                    </Typography>
                  ) : (
                    <Box sx={{ pl: 1.25, mb: 0.5 }}>
                      {formatFilters(item).map(([key, value]) => (
                        <Typography key={`${item.campaign_id}-${key}`} sx={{ fontFamily: 'inter', fontSize: '11px', color: '#1E293B' }}>
                          {key}: {String(value)}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  <Typography sx={{ fontFamily: 'inter', fontSize: '11px', color: '#1E293B', mb: 0.5 }}>
                    <strong>Subject:</strong> {item.subject || '-'}
                  </Typography>
                  <Typography sx={{ fontFamily: 'inter', fontSize: '11px', color: '#1E293B', mb: 0.5, whiteSpace: 'pre-wrap' }}>
                    <strong>Content:</strong> {item.content || '-'}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>
      </Box>

      <Dialog open={sendConfirmOpen} onClose={closeSendConfirm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'inter', fontWeight: 600 }}>Confirm send</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'inter', fontSize: '14px', fontWeight: 600, color: '#1E293B', mb: 1 }}>
            Recipient criteria
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              mb: 2,
              pl: 2.5,
              fontFamily: 'inter',
              fontSize: '14px',
              color: '#334155',
              '& li': { mb: 0.75 },
            }}
          >
            {recipientFilterRows.map((row) => (
              <Box component="li" key={row.id}>
                <strong>{row.label}:</strong> {row.value}
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontFamily: 'inter', fontSize: '14px', fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
            Matching recipients
          </Typography>
          {previewCountLoading ? (
            <Typography sx={{ fontFamily: 'inter', fontSize: '14px', color: '#64748B' }}>Calculating…</Typography>
          ) : previewCountError ? (
            <Typography sx={{ fontFamily: 'inter', fontSize: '14px', color: '#B91C1C' }}>{previewCountError}</Typography>
          ) : (
            <DialogContentText sx={{ fontFamily: 'inter', fontSize: '15px', color: '#1E293B', m: 0 }}>
              <strong>{recipientPreviewCount}</strong> member{recipientPreviewCount === 1 ? '' : 's'} with a valid email
              will be queued for this campaign.
              {recipientPreviewCount === 0 ? (
                <Typography component="span" sx={{ display: 'block', mt: 1, color: '#B91C1C', fontSize: '14px' }}>
                  No recipients match. Adjust filters before sending.
                </Typography>
              ) : null}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeSendConfirm} disabled={sending} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSend()}
            variant="contained"
            disabled={
              sending ||
              previewCountLoading ||
              previewCountError ||
              recipientPreviewCount == null ||
              recipientPreviewCount === 0
            }
            sx={{ background: '#288d2c', '&:hover': { background: '#237a26' }, textTransform: 'none' }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CommunicationCompose;

